import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const GallerySection = () => {
  const { t } = useLanguage();

  const images = [
    { src: '/gallery/img1.jpg', alt: 'Zodiac crab illustration', w: 351, h: 387 },
    { src: '/gallery/img4.jpg', alt: 'Golden figure with stars', w: 500, h: 725 },
    { src: '/gallery/img5.jpg', alt: 'Moon and rabbit drawing', w: 828, h: 814 },
    { src: '/gallery/img3.jpg', alt: 'Swallows and flower', w: 1200, h: 1200 },
    { src: '/gallery/img2.jpg', alt: 'Hand holding mirror', w: 496, h: 630 },
    { src: '/gallery/img6.jpg', alt: 'Ethereal embrace', w: 978, h: 1200 },
    { src: '/gallery/img7.jpg', alt: 'Sky within silhouette', w: 736, h: 927 },
    { src: '/gallery/img8.jpg', alt: 'Seed of growth', w: 1080, h: 1119 },
    { src: '/gallery/img9.jpg', alt: 'Lips impression', w: 1044, h: 871 },
  ];

  return (
    <section id="gallery" className="py-24 bg-[#faf7ee]/40 overflow-hidden">
      <div className="container mx-auto px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl mb-6 text-neutral-900">{t('galleryTitle')}</h2>
            <p className="text-base text-neutral-600 max-w-2xl mx-auto leading-relaxed">
              {t('gallerySubtitle')}
            </p>
          </motion.div>

          {/* Masonry gallery with stagger animation */}
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4">
            {images.map((img, index) => (
              <motion.div 
                key={index}
                className="break-inside-avoid mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <motion.div 
                  className="relative group overflow-hidden shadow-lg"
                  whileHover={{ scale: 1.02 }}
                >
                  <img 
                    src={img.src} 
                    alt={img.alt}
                    width={img.w}
                    height={img.h}
                    className="w-full h-auto object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <motion.div 
                    className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300"
                    initial={false}
                  />
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* Pinterest link */}
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <motion.a 
              href="https://hu.pinterest.com/melindatoth0302/soulboard/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#E60023] text-white rounded-full font-medium"
              whileHover={{ scale: 1.05, backgroundColor: '#ad081b' }}
              whileTap={{ scale: 0.95 }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
              </svg>
              {t('viewOnPinterest')}
              <ExternalLink size={16} />
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
